import { useState } from "react";
import { motion } from "motion/react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { 
  CheckCircle, 
  Clock, 
  Copy, 
  Download,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Building2,
  Bitcoin,
  ArrowLeft,
  FileText,
  Users
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
interface PaymentConfirmationPageProps {
  onNavigateBack: () => void;
  onNavigateToHome: () => void;
  orderData: any;
}

export function PaymentConfirmationPage({ 
  onNavigateBack, 
  onNavigateToHome, 
  orderData 
}: PaymentConfirmationPageProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const getPaymentInstructions = () => {
    switch (orderData.metodoPago) {
      case 'transferencia':
        return {
          title: 'Transferencia Bancaria',
          icon: Building2,
          instructions: [
            'Realiza la transferencia a la siguiente cuenta bancaria:',
            'Banco: Banco Santander',
            'IBAN: ES91 2100 0418 4502 0005 1332',
            'BIC/SWIFT: CAIXESBBXXX',
            'Titular: RebelGenomics S.L.',
            'Concepto: ' + orderData.orderId,
            'Importe: ' + orderData.total + '€'
          ],
          copyData: [
            { label: 'IBAN', value: 'ES91 2100 0418 4502 0005 1332' },
            { label: 'BIC/SWIFT', value: 'CAIXESBBXXX' },
            { label: 'Concepto', value: orderData.orderId }
          ]
        };
      case 'btcpay':
        return {
          title: 'Pago con Bitcoin (BTCPay)',
          icon: Bitcoin,
          instructions: [
            'Accede al siguiente enlace para completar el pago:',
            'https://btcpay.rebelgenomics.com/pay/' + orderData.orderId,
            'El pago se procesará automáticamente una vez confirmado en la blockchain',
            'Recibirás una confirmación inmediata en tu email'
          ],
          copyData: [
            { label: 'Enlace de pago', value: 'https://btcpay.rebelgenomics.com/pay/' + orderData.orderId }
          ]
        };
      case 'wise':
        return {
          title: 'Pago con Wise',
          icon: CreditCard,
          instructions: [
            'Accede a tu cuenta de Wise y realiza el pago a:',
            'Email: payments@rebelgenomics.com',
            'Importe: ' + orderData.total + '€',
            'Concepto: ' + orderData.orderId,
            'El pago se procesará inmediatamente'
          ],
          copyData: [
            { label: 'Email Wise', value: 'payments@rebelgenomics.com' },
            { label: 'Concepto', value: orderData.orderId }
          ]
        };
      default:
        return null;
    }
  };

  const paymentInfo = getPaymentInstructions();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const selectedFecha = orderData.curso?.fechas?.find((f: any) => f.id === orderData.fecha);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onNavigateBack}
              className="flex items-center text-gray-600 hover:text-orange-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span>Volver</span>
            </button>
            
            <div className="flex items-center">
              <img src={rebelLogo} alt="Rebel Genomics" className="w-8 h-8 mr-2" />
              <span className="text-xl text-gray-800">RebelGenomics</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-6xl mb-4 text-green-600">
              ¡INSCRIPCIÓN
              <br />
              <span className="text-black">CONFIRMADA!</span>
            </h1>
            <p className="text-xl text-gray-700 mb-4">
              Tu solicitud ha sido registrada correctamente
            </p>
            <Badge className="bg-orange-100 text-orange-800 text-base px-4 py-2">
              Pedido #{orderData.orderId}
            </Badge>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Payment Instructions */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Card className="p-8 rounded-3xl shadow-lg">
                <div className="flex items-center mb-6">
                  {paymentInfo && <paymentInfo.icon className="w-8 h-8 text-orange-500 mr-3" />}
                  <h2 className="text-2xl text-gray-800">
                    {paymentInfo?.title || 'Instrucciones de Pago'}
                  </h2>
                </div>

                {paymentInfo && (
                  <div className="space-y-6">
                    <div className="bg-orange-50 p-6 rounded-2xl">
                      <div className="space-y-3">
                        {paymentInfo.instructions.map((instruction, index) => (
                          <p key={index} className="text-gray-700">
                            {instruction}
                          </p>
                        ))}
                      </div>
                    </div>

                    {/* Copy Data Section */}
                    {paymentInfo.copyData && paymentInfo.copyData.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-lg font-medium text-gray-800">Datos para copiar:</h4>
                        {paymentInfo.copyData.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-100 rounded-xl">
                            <div>
                              <p className="text-sm text-gray-600">{item.label}</p>
                              <p className="font-mono text-gray-800">{item.value}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(item.value, item.label)}
                              className="ml-4"
                            >
                              {copied === item.label ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-8 p-6 bg-blue-50 rounded-2xl">
                  <div className="flex items-start">
                    <Clock className="w-6 h-6 text-blue-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Próximos pasos:</h4>
                      <ul className="text-blue-700 text-sm space-y-1">
                        <li>1. Completa el pago siguiendo las instrucciones</li>
                        <li>2. Recibirás confirmación de pago en tu email</li>
                        <li>3. Te enviaremos las instrucciones del curso 48h antes</li>
                        <li>4. ¡Disfruta de tu formación en cultivo in vitro!</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Card className="p-8 rounded-3xl shadow-lg">
                <h3 className="text-2xl mb-6 text-gray-800">Resumen de tu Inscripción</h3>
                
                <div className="space-y-6">
                  {/* Course Info */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">{orderData.curso?.name}</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2" />
                        {orderData.curso?.level} • {orderData.curso?.duration}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        {orderData.curso?.format}
                      </div>
                      {selectedFecha && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          {selectedFecha.date} • {selectedFecha.time}
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* Student Info */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Datos del estudiante</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-gray-700">{orderData.correo}</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-gray-700">{orderData.telefono}</span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Pricing */}
                  <div className="space-y-2">
                    {orderData.curso?.originalPrice && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Precio original:</span>
                        <span className="line-through text-gray-400">{orderData.curso.originalPrice}€</span>
                      </div>
                    )}
                    {orderData.curso?.savings && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">Descuento aplicado:</span>
                        <span className="text-green-600">-{orderData.curso.savings}€</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold pt-4 border-t">
                      <span>Total pagado:</span>
                      <span className="text-orange-500">{orderData.total}€</span>
                    </div>
                  </div>

                  {/* What's Included */}
                  <div className="bg-green-50 p-6 rounded-2xl">
                    <h4 className="font-semibold text-green-800 mb-3">Incluido en tu curso:</h4>
                    <ul className="space-y-2 text-sm text-green-700">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Grabación completa del curso
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Material PDF descargable
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Certificado de finalización
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Acceso a comunidad privada
                      </li>
                      {orderData.curso?.id === 'presencial' && (
                        <li className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Material de laboratorio incluido
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </Card>

              {/* Contact Info */}
              <Card className="p-6 rounded-2xl shadow-lg mt-6 bg-orange-50 border-orange-200">
                <h4 className="font-semibold text-orange-800 mb-3">¿Necesitas ayuda?</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-orange-600 mr-2" />
                    <span className="text-orange-700">hello@rebelgenomics.com</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-orange-600 mr-2" />
                    <span className="text-orange-700">+34 667 738 259</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-12"
          >
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                onClick={onNavigateToHome}
              >
                VOLVER AL INICIO
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white text-xl px-12 py-6 rounded-full"
              >
                <Download className="w-5 h-5 mr-2" />
                DESCARGAR RECIBO
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}